import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useApiClientWrapper } from 'hooks';
import { RootState } from 'store';
import { fetchAutomationCriteriaThunk } from '@spike/automations-action';
import { type CriteriaCatalog, type Criterion, type CriteriaCategory } from '@spike/automation-model';

/**
 * The criteria catalog for the current marketplace, fetched once per session.
 *
 * Replaces `criteriaDefinitions.ts`, which offered pet sizes (`extra_small`)
 * and genders (`unknown`) the database never stored, so those filters matched
 * nobody. Options now arrive narrowed to the marketplace, in the backend's
 * vocabulary.
 */
export interface CriteriaCatalogState {
    catalog?: CriteriaCatalog;
    fields: Criterion[];
    categories: CriteriaCatalog['categories'];
    grouped: Array<{ category: CriteriaCategory; label: string; fields: Criterion[] }>;
    byField: Record<string, Criterion>;
    loading: boolean;
    unavailable: boolean;
}

export const useCriteriaCatalog = (): CriteriaCatalogState => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const catalog = useSelector((state: RootState) => state.automations.criteria);
    const loading = useSelector((state: RootState) => state.automations.loading);

    // useApiClientWrapper() builds a new object on every render, so it must not
    // be a dependency: the effect would refire each render. The ref also stops a
    // second request while the first is still in flight — the `catalog` guard
    // can't do that on its own, since it only flips once a response lands.
    const requested = useRef(false);

    useEffect(() => {
        if (requested.current) return;
        requested.current = true;

        (dispatch as any)(fetchAutomationCriteriaThunk(apiClientWrapper)).catch(() => {
            requested.current = false;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalog]);

    return useMemo(() => {
        const fields = catalog?.fields ?? [];
        const categories = catalog?.categories ?? [];

        const grouped = [...categories]
            .sort((a, b) => a.order - b.order)
            .map(category => ({
                category: category.id,
                label: category.label,
                fields: fields.filter(field => field.category === category.id)
            }))
            .filter(group => group.fields.length > 0);

        const byField = Object.fromEntries(fields.map(field => [field.field, field]));

        return {
            catalog,
            fields,
            categories,
            grouped,
            byField,
            loading: loading && !catalog,
            unavailable: !loading && !catalog
        };
    }, [catalog, loading]);
};

export default useCriteriaCatalog;
